// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { getBillingSummary, getFicheClient, updateIdentiteClient } from '../services/api';
import ResumePoolLicence from './ResumePoolLicence';

/**
 * Fiche client d'un compte, ouverte au clic sur son e-mail dans l'administration
 * (chantier SaaS 5bis, demande de Marc du 16/09 puis du 23/09/2026 : « une fiche
 * client classique »). Quatre onglets : Administratif, Abonnement, Factures,
 * Consommation.
 *
 * ⚠️ RÉSERVÉE À EVALENS : la route répond 403 à tout autre rôle. ⛔ Ne jamais
 * l'ouvrir depuis l'espace client d'une licence — un employeur y lirait
 * l'activité individuelle d'un salarié (cadrage transverse n° 1).
 *
 * `onOuvrirFiche` permet de passer d'un collègue de Cabinet à la fiche de son
 * titulaire, seul endroit où l'identité du cabinet se modifie.
 */

const CHAMPS_IDENTITE = [
  ['raison_sociale', 'fiche_raison_sociale'], ['siren', 'fiche_siren'], ['siret', 'fiche_siret'],
  ['tva_intracom', 'fiche_tva'], ['adresse', 'fiche_adresse'], ['code_postal', 'fiche_code_postal'],
  ['ville', 'fiche_ville'], ['pays', 'fiche_pays'], ['email_facturation', 'fiche_email_facturation'],
  ['telephone', 'fiche_telephone'],
];

const ONGLETS = [
  ['admin', 'fiche_onglet_admin'], ['abonnement', 'fiche_onglet_abonnement'],
  ['factures', 'fiche_onglet_factures'], ['conso', 'fiche_onglet_conso'],
];

const moisCourant = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const styles = {
  label: { fontSize: 11, color: 'var(--text-muted)', margin: 0 },
  valeur: { fontSize: 14, color: 'var(--black)', margin: '2px 0 0' },
  section: { fontSize: 14, fontWeight: 700, color: 'var(--black)', margin: '0 0 10px' },
  grille: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 },
  th: { padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--gray)' },
  td: { padding: '8px 10px', color: 'var(--black)', borderBottom: '1px solid var(--gray)' },
  note: { fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' },
  bouton: { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 13, cursor: 'pointer' },
  input: { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 13 },
};

const FicheClient = ({ userId, onClose, onOuvrirFiche }) => {
  const { t, lang } = useI18n();
  const [fiche, setFiche] = useState(null);
  const [erreur, setErreur] = useState('');
  const [onglet, setOnglet] = useState('admin');
  const [edition, setEdition] = useState(null);      // brouillon de l'identité, ou null
  const [envoi, setEnvoi] = useState(false);
  const [erreurEdition, setErreurEdition] = useState('');
  const [moisPool, setMoisPool] = useState(moisCourant());
  const [pool, setPool] = useState(undefined);        // undefined = pas chargé, null = absent

  const locale = lang === 'en' ? 'en-GB' : 'fr-FR';
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
  const euros = (centimes) => (centimes == null ? '—' : `${(centimes / 100).toFixed(2)} €`);

  const charger = useCallback(async () => {
    setErreur(''); setFiche(null);
    try { setFiche(await getFicheClient(userId)); }
    catch (e) { setErreur(e.message); }
  }, [userId]);

  useEffect(() => { setOnglet('admin'); setEdition(null); charger(); }, [charger]);

  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [onClose]);

  // Le pool partagé d'un Cabinet : repris tel quel de la vue Rentabilité, sa
  // seule source — rien n'est recalculé ici (décision de Marc, 22/09/2026).
  const licenceCabinet = fiche?.licence?.est_cabinet ? fiche.licence.id : null;
  useEffect(() => {
    if (onglet !== 'conso' || !licenceCabinet) return;
    let annule = false;
    setPool(undefined);
    getBillingSummary(moisPool, 'mois')
      .then(d => { if (!annule) setPool((d.licences || []).find(l => l.license_id === licenceCabinet) || null); })
      .catch(() => { if (!annule) setPool(null); });
    return () => { annule = true; };
  }, [onglet, licenceCabinet, moisPool]);

  const enregistrer = async () => {
    setEnvoi(true); setErreurEdition('');
    try {
      const { identite } = await updateIdentiteClient(userId, edition);
      setFiche(f => ({ ...f, identite }));
      setEdition(null);
    } catch (e) { setErreurEdition(e.message); }
    finally { setEnvoi(false); }
  };

  const libelleStatut = (statut) => ({
    active: t('fiche_statut_active'), trialing: t('fiche_statut_trialing'),
    past_due: t('users_abo_retard'), unpaid: t('users_abo_impaye'),
    canceled: t('users_abo_resilie'), incomplete_expired: t('users_abo_expire'),
    incomplete: t('fiche_statut_incomplete'),
  }[statut] || t('fiche_statut_none'));

  const libelleOrigine = (origine) => {
    if (!origine) return '—';
    if (origine.startsWith('admin:')) return t('fiche_origine_evt_admin').replace('{email}', origine.slice(6));
    if (origine.startsWith('webhook:')) return t('fiche_origine_evt_stripe');
    if (origine === 'confirmation_checkout') return t('fiche_origine_evt_client');
    if (origine === 'resynchronisation') return t('fiche_origine_evt_resync');
    return origine;
  };

  const detailEvenement = (e) => {
    const parties = [];
    if (e.palier_avant !== e.palier_apres) parties.push(`${e.palier_avant || '—'} → ${e.palier_apres || '—'}`);
    if (e.statut_avant !== e.statut_apres) parties.push(`${libelleStatut(e.statut_avant)} → ${libelleStatut(e.statut_apres)}`);
    if (e.details && e.details.startsWith('fin=')) parties.push(t('fiche_fin_prevue').replace('{date}', date(e.details.slice(4))));
    return parties.join(' · ') || '—';
  };

  const libelleFacture = (statut) => ({
    paid: t('fiche_facture_paid'), open: t('fiche_facture_open'), void: t('fiche_facture_void'),
    draft: t('fiche_facture_draft'), uncollectible: t('fiche_facture_uncollectible'),
  }[statut] || statut || '—');

  const libelleAnalyse = (statut) => (
    statut === 'COMPLETED' ? t('fiche_analyse_terminee')
      : statut === 'ERROR' ? t('fiche_analyse_erreur')
      : t('fiche_analyse_en_cours'));

  const libelleHorsForfait = (statut) => (
    statut === 'envoye' ? t('fiche_hf_envoye')
      : statut === 'echec' ? t('fiche_hf_echec')
      : t('fiche_hf_en_attente'));

  const Champ = ({ libelle, valeur }) => (
    <div>
      <p style={styles.label}>{libelle}</p>
      <p style={{ ...styles.valeur, color: valeur ? 'var(--black)' : 'var(--text-muted)' }}>{valeur || t('fiche_non_renseigne')}</p>
    </div>
  );

  // ─── Onglets ────────────────────────────────────────────────────────────────

  const ongletAdmin = () => {
    const { compte, identite, licence } = fiche;
    const cgu = compte.cgu_accepted_at
      ? t('fiche_cgu_valeur').replace('{v}', compte.cgu_version || '?').replace('{date}', date(compte.cgu_accepted_at))
      : t('fiche_cgu_jamais');
    const noteOrigine = {
      stripe: t('fiche_origine_stripe'),
      admin: t('fiche_origine_admin').replace('{par}', identite.maj_par || '?').replace('{date}', date(identite.maj_le)),
      client: t('fiche_origine_client'),
      licence: t('fiche_origine_licence'),
    }[identite.origine];
    return (
      <>
        <h3 style={styles.section}>{t('fiche_section_compte')}</h3>
        <div style={styles.grille}>
          <Champ libelle={t('fiche_email')} valeur={compte.email} />
          <Champ libelle={t('fiche_nom_affiche')} valeur={compte.display_name} />
          <Champ libelle={t('fiche_cree_le')} valeur={date(compte.created_at)} />
          <Champ libelle={t('fiche_etat')} valeur={compte.is_active ? t('fiche_actif') : t('fiche_revoque_le').replace('{date}', date(compte.revoked_at))} />
          <Champ libelle={t('fiche_cgu')} valeur={cgu} />
          <Champ libelle={t('fiche_mfa')} valeur={compte.mfa_enabled ? t('fiche_oui') : t('fiche_non')} />
          {licence && <Champ libelle={t('fiche_licence')} valeur={`${licence.nom} (${licence.code})`} />}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 10px' }}>
          <h3 style={{ ...styles.section, margin: 0 }}>{t('fiche_section_identite')}</h3>
          {fiche.identite_modifiable && !edition && (
            <button style={styles.bouton} onClick={() => { setErreurEdition(''); setEdition(Object.fromEntries(CHAMPS_IDENTITE.map(([c]) => [c, identite[c] || '']))); }}>
              ✏️ {t('fiche_modifier')}
            </button>
          )}
        </div>

        {edition ? (
          <>
            <div style={styles.grille}>
              {CHAMPS_IDENTITE.map(([champ, cle]) => (
                <label key={champ}>
                  <span style={styles.label}>{t(cle)}</span>
                  <input style={styles.input} value={edition[champ]}
                    onChange={e => setEdition(d => ({ ...d, [champ]: e.target.value }))} />
                </label>
              ))}
            </div>
            {erreurEdition && <p style={{ ...styles.note, color: '#ef4444' }}>❌ {erreurEdition}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button style={{ ...styles.bouton, background: 'var(--primary, #2563eb)', color: '#fff', border: 'none' }}
                disabled={envoi} onClick={enregistrer}>{t('fiche_enregistrer')}</button>
              <button style={styles.bouton} disabled={envoi} onClick={() => setEdition(null)}>{t('fiche_annuler')}</button>
            </div>
          </>
        ) : (
          <>
            <div style={styles.grille}>
              {CHAMPS_IDENTITE.map(([champ, cle]) => <Champ key={champ} libelle={t(cle)} valeur={identite[champ]} />)}
            </div>
            {fiche.identite_source === 'titulaire_cabinet' && (
              <p style={styles.note}>
                {t('fiche_source_titulaire').replace('{email}', fiche.identite_payeur_email || '?')}{' '}
                {fiche.identite_payeur_id && onOuvrirFiche && (
                  <button style={{ ...styles.bouton, padding: '2px 8px', fontSize: 12 }}
                    onClick={() => onOuvrirFiche(fiche.identite_payeur_id)}>{t('fiche_ouvrir')}</button>
                )}
              </p>
            )}
            {noteOrigine && <p style={styles.note}>{noteOrigine}</p>}
            {fiche.identite_source !== 'licence' && !identite.siren && (
              <p style={{ ...styles.note, color: '#b45309' }}>⚠️ {t('fiche_siren_manquant')}</p>
            )}
          </>
        )}
      </>
    );
  };

  const ongletAbonnement = () => {
    const { compte, licence, historique } = fiche;
    if (fiche.identite_source === 'licence') {
      return <p style={styles.note}>{t('fiche_abo_licence').replace('{nom}', licence?.nom || '?')}</p>;
    }
    return (
      <>
        <div style={styles.grille}>
          <Champ libelle={t('fiche_palier_actuel')} valeur={licence?.est_cabinet ? t('ent_badge_cabinet') : (compte.tier_nom || t('fiche_aucun_palier'))} />
          <Champ libelle={t('fiche_statut')} valeur={libelleStatut(compte.subscription_status)} />
          <Champ libelle={t('fiche_fin_periode')} valeur={compte.subscription_current_period_end ? date(compte.subscription_current_period_end) : null} />
        </div>
        <h3 style={{ ...styles.section, margin: '24px 0 10px' }}>{t('fiche_historique_titre')}</h3>
        {fiche.identite_source === 'titulaire_cabinet' && <p style={{ ...styles.note, marginTop: 0, marginBottom: 8 }}>{t('fiche_historique_titulaire')}</p>}
        {historique.length === 0 ? (
          <p style={styles.note}>{t('fiche_historique_vide')}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['fiche_col_date', 'fiche_col_evenement', 'fiche_col_detail', 'fiche_col_origine'].map(k => <th key={k} style={styles.th}>{t(k)}</th>)}</tr></thead>
            <tbody>
              {historique.map((e, i) => (
                <tr key={i}>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{date(e.survenu_le)}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{t(`fiche_evt_${e.type}`)}</td>
                  <td style={styles.td}>{detailEvenement(e)}</td>
                  <td style={{ ...styles.td, color: 'var(--text-muted)' }}>{libelleOrigine(e.origine)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  };

  const ongletFactures = () => {
    const { factures_stripe: stripe, factures_qonto: qonto } = fiche;
    if (fiche.identite_source === 'licence') {
      return (
        <>
          <h3 style={styles.section}>{t('fiche_factures_qonto')}</h3>
          {qonto.length === 0 ? <p style={styles.note}>{t('fiche_factures_vide')}</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['fiche_col_numero', 'fiche_col_periode', 'fiche_col_ht', 'fiche_col_ttc', 'fiche_statut'].map(k => <th key={k} style={styles.th}>{t(k)}</th>)}</tr></thead>
              <tbody>
                {qonto.map(f => (
                  <tr key={f.id}>
                    <td style={styles.td}>{f.qonto_invoice_number || '—'}</td>
                    <td style={styles.td}>{date(f.periode_debut)} → {date(f.periode_fin)}</td>
                    {/* QONTO : montants en EUROS (révision 0010), pas en centimes. */}
                    <td style={styles.td}>{f.montant_ht?.toFixed(2)} €</td>
                    <td style={styles.td}>{f.montant_ttc?.toFixed(2)} €</td>
                    <td style={styles.td}>{f.statut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      );
    }
    return (
      <>
        {fiche.identite_source === 'titulaire_cabinet' && (
          <p style={{ ...styles.note, marginTop: 0, marginBottom: 8 }}>{t('fiche_factures_titulaire').replace('{email}', fiche.identite_payeur_email || '?')}</p>
        )}
        {stripe.length === 0 ? <p style={styles.note}>{t('fiche_factures_vide')}</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['fiche_col_numero', 'fiche_col_periode', 'fiche_col_ht', 'fiche_col_ttc', 'fiche_col_paye', 'fiche_statut', 'fiche_col_pdf'].map(k => <th key={k} style={styles.th}>{t(k)}</th>)}</tr></thead>
            <tbody>
              {stripe.map(f => (
                <tr key={f.id}>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{f.numero || '—'}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{date(f.periode_debut)} → {date(f.periode_fin)}</td>
                  {/* Stripe : montants en CENTIMES (révision 0019). */}
                  <td style={styles.td}>{euros(f.montant_ht)}</td>
                  <td style={styles.td}>{euros(f.montant_ttc)}</td>
                  <td style={styles.td}>{euros(f.montant_paye)}</td>
                  <td style={styles.td}>{libelleFacture(f.statut)}</td>
                  <td style={styles.td}>
                    {(f.hosted_invoice_url || f.invoice_pdf)
                      ? <a href={f.hosted_invoice_url || f.invoice_pdf} target="_blank" rel="noopener noreferrer">{t('fiche_voir_facture')}</a>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  };

  const ongletConso = () => {
    const { analyses, analyses_par_mois: parMois, hors_forfait: hf } = fiche;
    return (
      <>
        {licenceCabinet && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <h3 style={{ ...styles.section, margin: 0 }}>{t('billing_pool_title')}</h3>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {t('fiche_pool_mois')}{' '}
                <input type="month" value={moisPool} onChange={e => e.target.value && setMoisPool(e.target.value)} style={{ ...styles.input, width: 'auto' }} />
              </label>
            </div>
            <p style={{ ...styles.note, marginTop: 0, marginBottom: 8 }}>{t('billing_pool_explication_cabinet')}</p>
            {pool === undefined ? <p style={styles.note}>{t('fiche_chargement')}</p>
              : pool ? <ResumePoolLicence licence={pool} />
              : <p style={styles.note}>{t('fiche_pool_indisponible')}</p>}
          </div>
        )}

        <p style={{ ...styles.valeur, fontWeight: 600, marginBottom: 12 }}>{t('fiche_conso_total').replace('{n}', fiche.nb_analyses_total)}</p>

        {parMois.length > 0 && (
          <>
            <h3 style={styles.section}>{t('fiche_conso_par_mois')}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {parMois.map(m => (
                <span key={m.mois} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 99, background: 'var(--bg-input)', border: '1px solid var(--gray)', color: 'var(--black)' }}>
                  {m.mois} · <strong>{m.nb}</strong>
                </span>
              ))}
            </div>
          </>
        )}

        <h3 style={styles.section}>{t('fiche_hf_titre')}</h3>
        {hf.length === 0 ? <p style={{ ...styles.note, marginBottom: 20 }}>{t('fiche_hf_vide')}</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
            <thead><tr>{['fiche_col_date', 'fiche_col_rang', 'fiche_col_prix', 'fiche_statut'].map(k => <th key={k} style={styles.th}>{t(k)}</th>)}</tr></thead>
            <tbody>
              {hf.map((h, i) => (
                <tr key={i}>
                  <td style={styles.td}>{date(h.created_at)}</td>
                  <td style={styles.td}>{h.rang_dans_le_mois ?? '—'}</td>
                  <td style={styles.td}>{h.prix_unitaire_eur != null ? `${h.prix_unitaire_eur.toFixed(2)} €` : '—'}</td>
                  <td style={styles.td}>{libelleHorsForfait(h.statut)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3 style={styles.section}>{t('fiche_conso_liste').replace('{n}', analyses.length)}</h3>
        {analyses.length === 0 ? <p style={styles.note}>{t('fiche_conso_vide')}</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['fiche_col_fichier', 'fiche_col_depose', 'fiche_statut'].map(k => <th key={k} style={styles.th}>{t(k)}</th>)}</tr></thead>
            <tbody>
              {analyses.map(a => (
                <tr key={a.id}>
                  <td style={{ ...styles.td, wordBreak: 'break-all' }}>{a.filename}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{date(a.upload_date)}</td>
                  <td style={styles.td}>{libelleAnalyse(a.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  };

  // ─── Rendu ──────────────────────────────────────────────────────────────────

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="fiche-client-titre" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-surface)', borderRadius: 12, width: 900, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 22px 0' }}>
          <div>
            <div id="fiche-client-titre" style={{ fontWeight: 700, fontSize: 17, color: 'var(--black)' }}>
              {fiche?.identite?.raison_sociale || fiche?.compte?.email || '…'}
            </div>
            {fiche?.identite?.raison_sociale && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{fiche.compte.email}</div>}
          </div>
          <button onClick={onClose} aria-label={t('fiche_fermer')} title={t('fiche_fermer')}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
        </div>

        <div role="tablist" style={{ display: 'flex', gap: 4, padding: '12px 22px 0', borderBottom: '1px solid var(--gray)' }}>
          {ONGLETS.map(([cle, libelle]) => (
            <button key={cle} role="tab" aria-selected={onglet === cle} onClick={() => setOnglet(cle)}
              style={{ padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
                       fontWeight: onglet === cle ? 700 : 400, color: onglet === cle ? 'var(--black)' : 'var(--text-muted)',
                       borderBottom: onglet === cle ? '2px solid var(--primary, #2563eb)' : '2px solid transparent', marginBottom: -1 }}>
              {t(libelle)}
            </button>
          ))}
        </div>

        <div role="tabpanel" style={{ padding: 22, overflowY: 'auto' }}>
          {erreur ? <p style={{ color: '#ef4444' }}>❌ {erreur}</p>
            : !fiche ? <p style={styles.note}>{t('fiche_chargement')}</p>
            : onglet === 'admin' ? ongletAdmin()
            : onglet === 'abonnement' ? ongletAbonnement()
            : onglet === 'factures' ? ongletFactures()
            : ongletConso()}
        </div>
      </div>
    </div>
  );
};

export default FicheClient;
